import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import {
  MessageSquare, History, Bell, Users, Send, Check,
  CheckCheck, Clock, AlertTriangle, Loader2, RefreshCw,
  FileText, User, Calendar, Filter, X
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Colaboracion() {
  const { toast } = useToast();
  const [tabActiva, setTabActiva] = useState('notificaciones');
  const [notificaciones, setNotificaciones] = useState([]);
  const [noLeidas, setNoLeidas] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expedienteId, setExpedienteId] = useState('');
  const [comentarios, setComentarios] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [nuevoComentario, setNuevoComentario] = useState('');

  useEffect(() => {
    cargarNotificaciones();
  }, []);

  const cargarNotificaciones = async () => {
    try {
      const response = await axios.get(`${API}/colaboracion/notificaciones`);
      setNotificaciones(response.data.notificaciones || []);
      setNoLeidas(response.data.no_leidas || 0);
    } catch (err) {
      console.error('Error cargando notificaciones:', err);
    } finally {
      setLoading(false);
    }
  };

  const cargarComentarios = async () => {
    if (!expedienteId) return;
    try {
      const response = await axios.get(`${API}/colaboracion/comentarios/${expedienteId}`);
      setComentarios(response.data.comentarios || []);
    } catch (err) {
      console.error('Error cargando comentarios:', err);
    }
  };

  const cargarHistorial = async () => {
    if (!expedienteId) return;
    try {
      const response = await axios.get(`${API}/colaboracion/historial/expediente/${expedienteId}`);
      setHistorial(response.data.historial || []);
    } catch (err) {
      console.error('Error cargando historial:', err);
    }
  };

  const marcarLeida = async (id) => {
    try {
      await axios.put(`${API}/colaboracion/notificaciones/${id}/leer`);
      cargarNotificaciones();
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const marcarTodasLeidas = async () => {
    try {
      await axios.put(`${API}/colaboracion/notificaciones/leer-todas`);
      cargarNotificaciones();
      toast({ title: 'Notificaciones marcadas como leídas' });
    } catch (err) {
      toast({ title: 'Error', description: 'No se pudieron marcar las notificaciones', variant: 'destructive' });
    }
  };

  const enviarComentario = async () => {
    if (!nuevoComentario.trim() || !expedienteId) return;
    try {
      await axios.post(`${API}/colaboracion/comentarios`, {
        expediente_id: parseInt(expedienteId),
        contenido: nuevoComentario,
        mencion_usuarios: []
      });
      setNuevoComentario('');
      cargarComentarios();
      toast({ title: 'Comentario añadido' });
    } catch (err) {
      toast({ title: 'Error', description: 'No se pudo añadir el comentario', variant: 'destructive' });
    }
  };

  const getIconoNotificacion = (tipo) => {
    const iconos = {
      mencion: <MessageSquare className="h-4 w-4 text-blue-600" />,
      tarea_asignada: <Users className="h-4 w-4 text-green-600" />,
      plazo: <AlertTriangle className="h-4 w-4 text-yellow-600" />,
      cambio: <History className="h-4 w-4 text-purple-600" />
    };
    return iconos[tipo] || <Bell className="h-4 w-4 text-gray-600" />;
  };

  const getTipoCambioBadge = (tipo) => {
    const estilos = {
      crear: 'bg-green-100 text-green-700',
      modificar: 'bg-blue-100 text-blue-700',
      eliminar: 'bg-red-100 text-red-700'
    };
    return estilos[tipo] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Colaboración</h1>
        <p className="text-gray-600">Notificaciones, comentarios e historial de cambios</p>
      </div>

      <Tabs value={tabActiva} onValueChange={setTabActiva}>
        <TabsList className="mb-4">
          <TabsTrigger value="notificaciones" className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Notificaciones
            {noLeidas > 0 && (
              <Badge variant="destructive" className="ml-1">{noLeidas}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="comentarios" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Comentarios
          </TabsTrigger>
          <TabsTrigger value="historial" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            Historial
          </TabsTrigger>
        </TabsList>

        {/* Notificaciones */}
        <TabsContent value="notificaciones">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Notificaciones</CardTitle>
                <CardDescription>{noLeidas} sin leer</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={cargarNotificaciones}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
                {noLeidas > 0 && (
                  <Button variant="outline" size="sm" onClick={marcarTodasLeidas}>
                    <CheckCheck className="h-4 w-4 mr-1" /> Marcar todas
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {notificaciones.length > 0 ? (
                <div className="space-y-2">
                  {notificaciones.map((notif) => (
                    <div
                      key={notif.id}
                      className={`p-4 rounded-lg border flex items-start gap-3 ${
                        notif.leida ? 'bg-white' : 'bg-blue-50 border-blue-200'
                      }`}
                    >
                      <div className="mt-1">{getIconoNotificacion(notif.tipo)}</div>
                      <div className="flex-1">
                        <p className={`text-sm ${notif.leida ? 'text-gray-600' : 'text-gray-900 font-medium'}`}>
                          {notif.mensaje}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(notif.fecha).toLocaleString('es-ES')}
                        </p>
                      </div>
                      {!notif.leida && (
                        <Button variant="ghost" size="sm" onClick={() => marcarLeida(notif.id)}>
                          <Check className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Bell className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No hay notificaciones</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Comentarios */}
        <TabsContent value="comentarios">
          <Card>
            <CardHeader>
              <CardTitle>Comentarios de Expediente</CardTitle>
              <CardDescription>Comentarios internos del equipo</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="ID del expediente"
                    value={expedienteId}
                    onChange={(e) => setExpedienteId(e.target.value)}
                    className="w-40"
                  />
                  <Button onClick={cargarComentarios}>
                    Cargar comentarios
                  </Button>
                </div>
              </div>

              {comentarios.length > 0 ? (
                <div className="space-y-3 mb-4">
                  {comentarios.map((com) => (
                    <div key={com.id} className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <User className="h-4 w-4 text-blue-600" />
                        </div>
                        <span className="font-medium text-sm">{com.autor}</span>
                        <span className="text-xs text-gray-500">
                          {new Date(com.fecha).toLocaleString('es-ES')}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 ml-10">{com.contenido}</p>
                    </div>
                  ))}
                </div>
              ) : expedienteId ? (
                <p className="text-center text-gray-500 py-8">No hay comentarios en este expediente</p>
              ) : null}

              {expedienteId && (
                <div className="flex gap-2">
                  <Textarea
                    placeholder="Añadir comentario..."
                    value={nuevoComentario}
                    onChange={(e) => setNuevoComentario(e.target.value)}
                    className="flex-1"
                    rows={2}
                  />
                  <Button onClick={enviarComentario} disabled={!nuevoComentario.trim()}>
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Historial */}
        <TabsContent value="historial">
          <Card>
            <CardHeader>
              <CardTitle>Historial de Cambios</CardTitle>
              <CardDescription>Registro de modificaciones</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="ID del expediente"
                    value={expedienteId}
                    onChange={(e) => setExpedienteId(e.target.value)}
                    className="w-40"
                  />
                  <Button onClick={cargarHistorial}>
                    Ver historial
                  </Button>
                </div>
              </div>

              {historial.length > 0 ? (
                <div className="space-y-2">
                  {historial.map((h) => (
                    <div key={h.id} className="border-l-2 border-gray-200 pl-4 py-2">
                      <div className="flex items-center gap-2">
                        <Badge className={getTipoCambioBadge(h.tipo)}>
                          {h.tipo}
                        </Badge>
                        <span className="text-sm font-medium">{h.campo}</span>
                        <span className="text-xs text-gray-500">{h.usuario}</span>
                      </div>
                      <div className="text-xs mt-1">
                        {h.valor_anterior && (
                          <span className="text-red-600 line-through mr-2">{h.valor_anterior}</span>
                        )}
                        {h.valor_nuevo && (
                          <span className="text-green-600">{h.valor_nuevo}</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(h.fecha).toLocaleString('es-ES')}
                      </p>
                    </div>
                  ))}
                </div>
              ) : expedienteId ? (
                <p className="text-center text-gray-500 py-8">No hay historial para este expediente</p>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
